(ns app.front.view.inspections.add
  "A component that allows adding inspections to a workspace, either to
  permit or individually."
  (:require [app.domain.inspection.model :as inspection-model]
            [app.domain.util :as du]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.select :as ui-select]
            [app.front.ui.table :as ui-table]
            [app.front.util :as futil]
            [app.front.view.inspections.subs :as inspection-sub]
            [app.front.view.inspections.util :as inspection-util]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [re-frame.core-instrumented :as rf]))

(defn pick-entity-handler
  "subscription handler which operates on two upstream subscriptions,
  a collection of entities + an entity id, returns the entity that
  matches the id"
  [[all id] _]
  (-> (group-by :db/id all)
      (get id)
      first))

(defn select-any-template [kind]
  (->> kind
       :entity/_parent
       (filter inspection-model/is-inspection-template?)
       (filter du/is-active?)
       first
       :db/id))

(defn selectable-kinds-for-target [kinds target-entity pre-issuance-inspections?]
  (if pre-issuance-inspections?
    (filter (partial inspection-model/inspection-template-kind-applicable-to-target? target-entity) kinds)
    kinds))

;; Events
;;;;;;;;;

(rf/reg-event-fx ::add-done
  (fn [{:keys [db]} [_ also-dispatch status response]]
    (when (= 200 status)
      (cond-> {:db (dissoc db ::add)}
        also-dispatch (assoc :dispatch (conj also-dispatch response))))))

(rf/reg-event-db ::add-new
  (fn [db [_ defaults]]
    (-> db
        (assoc ::add defaults))))

(rf/reg-event-fx ::trigger-add-inspection
  (fn [{:keys [db]} [_ section-id]]
    (let [selectable-kinds (get-in db [:entities :inspection-template-kind])
          any-kind         (->> selectable-kinds vals (filter du/is-active?) first)
          defaults         {::kind-id     (:db/id any-kind)
                            ::template-id (select-any-template any-kind)}]
      {:db (assoc db ::add defaults)
       :fx [[::futil/scroll-to-section (or section-id :application-inspections)]]})))

(rf/reg-event-db ::select-kind
  (fn [db [_ kind]]
    (-> db
        (assoc-in [::add ::kind-id] (:db/id kind))
        (assoc-in [::add ::template-id] (select-any-template kind)))))

(rf/reg-event-db ::select-template
  (fn [db [_ template]]
    (-> db
        (assoc-in [::add ::template-id] (:db/id template)))))

;; Subscriptions
;;;;;;;;;;;;;;;;

(rf/reg-sub ::add
  :-> ::add)

(rf/reg-sub ::adding?
  :<- [::add]
  :-> some?)

(rf/reg-sub ::add-inspection-kind-id
  :<- [::add]
  :-> ::kind-id)

(rf/reg-sub ::add-inspection-template-id
  :<- [::add]
  :-> ::template-id)

(rf/reg-sub ::selected-inspection-kind
  (fn [_]
    [(rf/subscribe [::inspection-sub/selectable-inspection-kinds])
     (rf/subscribe [::add-inspection-kind-id])
     (rf/subscribe [::ff/enabled? :feature-flag.id/pre-issuance-inspections])])
  (fn [[kinds kind-id pre-issuance-inspections?] [_ target-entity]]
    (let [selectable-kinds (selectable-kinds-for-target kinds target-entity pre-issuance-inspections?)]
      (or (pick-entity-handler [selectable-kinds kind-id] nil)
          (first selectable-kinds)))))

(rf/reg-sub ::selectable-inspection-kinds
  :<- [::inspection-sub/selectable-inspection-kinds]
  :<- [::ff/enabled? :feature-flag.id/pre-issuance-inspections]
  (fn [[kinds pre-issuance-inspections?] [_ target-entity]]
    (selectable-kinds-for-target kinds target-entity pre-issuance-inspections?)))

(rf/reg-sub ::available-inspections
  (fn [[_ target-entity]]
    (rf/subscribe [::selected-inspection-kind target-entity]))
  (fn [kind _]
    (when kind
      (->> kind
           inspection-model/get-templates-for-kind))))

(rf/reg-sub ::show-template-selector
  (fn [[_ target-entity]]
    (rf/subscribe [::available-inspections target-entity]))
  (fn [templates]
    (-> templates
        count
        (> 1))))

(rf/reg-sub ::selected-inspection-template
  (fn [[_ target-entity]]
    [(rf/subscribe [::available-inspections target-entity])
     (rf/subscribe [::add-inspection-template-id])])
  (fn [[templates template-id] _]
    (or (pick-entity-handler [templates template-id] nil)
        (first templates))))

(rf/reg-sub ::selected-inspection-template-id
  (fn [[_ target-entity]]
    (rf/subscribe [::selected-inspection-template target-entity]))
  :-> :db/id)

(rf/reg-sub ::default-add-content
  (fn [[_ target-entity]]
    (rf/subscribe [::selectable-inspection-kinds target-entity]))
  (fn [[any-kind & _] _]
    {::kind-id     (:db/id any-kind)
     ::template-id (select-any-template any-kind)}))

(rf/reg-sub ::add-loading?
  (fn [_ [_ workspace-id]]
    [(rf/subscribe [::inspection-sub/fetching-inspections? workspace-id])
     (rf/subscribe [::process/signal-processing? :process.signal/add-inspection])])
  (fn [[fetching? adding?] _]
    (or fetching? adding?)))

;;; Components
;;;;;;;;;;;;;;

(defn add-selector [{:keys [tr verdict-section-id on-add-dispatch add-as-active? target-entity light]
                     :or   {add-as-active? false}}]
  (cond
    (<sub [::add-loading? (-> target-entity :entity/parent :workspace/id)])
    ;; if target is entity and we are loading, show spinner. ID only works too, but not with
    ;; spinner
    (cc/loading-spinner {:with-overlay false
                         :small        true})
    (empty? (<sub [::selectable-inspection-kinds target-entity]))
    [ui/warning (tr [:inspection/none-defined])]

    :else
    [:<>
     [ui-select/select {:id           "add-inspection-kind-selector"
                        :values       (<sub [::selectable-inspection-kinds target-entity])
                        :value        (<sub [::selected-inspection-kind target-entity])
                        :label-text   (tr [:verdict/inspection-type])
                        :val->str     #(inspection-model/inspection-template-kind-name
                                         % (<sub [:language]))
                        :light        (boolean light)
                        :allow-empty? false
                        :on-change    #(>evt [::select-kind %])}]
     (when (<sub [::show-template-selector target-entity])
       [ui-select/select {:id           "add-inspection-selector"
                          :values       (<sub [::available-inspections target-entity])
                          :value        (<sub [::selected-inspection-template target-entity])
                          :label-text   (tr [:inspection/select-inspection])
                          :val->str     (partial inspection-util/template->string tr)
                          :allow-empty? false
                          :on-change    #(>evt [::select-template %])
                          :light        (boolean light)}])
     (let [inspection-template-id (<sub [::selected-inspection-template-id target-entity])]
       (cc/button
         {:kind             :primary
          :icon             i/save
          :size             :field
          :class-name       "mt-4"
          :icon-description (tr [:inspection/add-new])
          :on-click         #(>evt [::process/signal
                                    target-entity
                                    [:process.signal/add-inspection
                                     {:verdict-section-id verdict-section-id
                                      :template-id        inspection-template-id
                                      :add-as-active?     add-as-active?}]
                                    {:on-done [::add-done on-add-dispatch]}])}
         (tr [:inspection/add-new])))]))

(defn add-component
  "A component for adding an inspection to the currently operated
  application (/workspace). Optional parameter `verdict-section-id`
  makes the inspection a permit condition.

  Requires opts `:tr` and `:target-entity`.

  Optional `:on-add-dispatch`"
  [props]
  (if (<sub [::adding?])
    [ui-table/add-to-table-row
     [add-selector props]]
    [ui-table/add-new-button
     (assoc props
            :on-click #(>evt [::add-new (<sub [::default-add-content (:target-entity props)])]))]))
